import { useEffect, useMemo, useState } from 'react'
import {
  Control,
  useController,
  useFormContext,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form'
import { RadioButtonSelect } from 'components/FormUtils/RadioButtonSelect'
import { Building2, MapPin } from 'lucide-react'
import { FieldHint } from '@firstbite/ui'
import { Tabs } from 'components/FbUI/Tabs'
import { SelectLocationsForDealTable } from 'components/Tables/SelectLocationsForDealTable/SelectLocationsForDealTable'
import { SelectLocationsPanelFbui } from './SelectLocationsPanelFbui'
import { type SelectDoorsStepFormValues } from './selectDoorsStep.schema'

// Schema + validation rules moved to a pure module (node-testable); re-exported
// here so existing `from './SelectDoorsStep'` imports keep working.
export {
  selectDoorsStepFormSchema,
  checkDoorSelectionRules,
} from './selectDoorsStep.schema'

interface SelectDoorsStepProps {
  chainProxyId: number
  companyTotalDoors: number
  radioGroupLabel?: string
  /** Render the @firstbite/ui location-picker panel (selected-door chips + an
   *  fb-ui `Table`) instead of the legacy controls. The scope radio lives in the
   *  modal's step 1 (not here) in this mode. Threaded from the fb-ui contact
   *  modal; deal modals leave it off so their look is unchanged. */
  ds2026?: boolean
}

export const SelectDoorsStep = ({
  chainProxyId,
  companyTotalDoors,
  radioGroupLabel,
  ds2026,
}: SelectDoorsStepProps) => {
  const { control, trigger: formTrigger } =
    useFormContext<SelectDoorsStepFormValues>()
  return (
    <SelectDoorsStepWithControl
      chainProxyId={chainProxyId}
      companyTotalDoors={companyTotalDoors}
      radioGroupLabel={radioGroupLabel}
      ds2026={ds2026}
      control={control}
      formTrigger={formTrigger}
    />
  )
}

interface SelectDoorsStepWithControlProps<
  T extends SelectDoorsStepFormValues,
> extends SelectDoorsStepProps {
  control: Control<T>
  formTrigger: UseFormTrigger<T>
}

export const SelectDoorsStepWithControl = <
  T extends SelectDoorsStepFormValues,
>({
  control: _control,
  chainProxyId,
  companyTotalDoors,
  formTrigger: _formTrigger,
  radioGroupLabel = 'Apply this deal to:',
  ds2026 = false,
}: SelectDoorsStepWithControlProps<T>) => {
  const control = _control as unknown as Control<SelectDoorsStepFormValues>
  const formTrigger =
    _formTrigger as unknown as UseFormTrigger<SelectDoorsStepFormValues>

  const tableKey = `SELECT_LOCATIONS_${chainProxyId}`

  const radioGroupOptions = useMemo(() => {
    return [
      {
        key: 'entire_company',
        icon: Building2,
        label: 'Entire Company',
      },
      {
        key: 'select_locations',
        icon: MapPin,
        label: 'Select locations',
        props: {
          disabled: companyTotalDoors === 1,
        },
        tooltip:
          companyTotalDoors === 1
            ? 'There is only one location for this company'
            : undefined,
      },
    ]
  }, [companyTotalDoors])

  const {
    field: { onChange: onChangeDoorIds, value: selectedIds = [] },
    fieldState: { error: doorIdsError },
  } = useController({ name: 'door_ids', control })

  const {
    field: { onChange: onChangeIsAllDoorsSelected },
  } = useController({ name: 'is_all_doors_selected', control })

  const applyTo = useWatch({ name: 'apply_to', control })

  const isSelectLocationsSelected = applyTo === 'select_locations'

  const [selectedTab, setSelectedTab] = useState<'selected' | 'not_selected'>(
    // With nothing selected yet, open on "Not Selected" (which lists the
    // pickable doors) rather than an empty "Selected (0)" tab. (ds2026 only —
    // legacy keeps its prior default.)
    ds2026 && selectedIds.length === 0 ? 'not_selected' : 'selected'
  )
  const [selectedCount, setSelectedCount] = useState(0)
  const [notSelectedCount, setNotSelectedCount] = useState(0)
  // ds2026: whether the user has touched the door selection yet. Until they do,
  // an empty selection is a not-yet-done step (guidance in the modal header),
  // not an error — so we only show the red validation hint after interaction.
  const [hasTouchedDoors, setHasTouchedDoors] = useState(false)

  useEffect(() => {
    // ds2026 derives is_all_doors_selected in its own change handler (below).
    if (ds2026) return
    if (!isSelectLocationsSelected) {
      onChangeIsAllDoorsSelected(false)
    }
  }, [isSelectLocationsSelected, ds2026])

  useEffect(() => {
    if (ds2026) return
    if (companyTotalDoors) {
      if (companyTotalDoors > 0 && selectedCount >= companyTotalDoors) {
        onChangeIsAllDoorsSelected(true)
      } else {
        onChangeIsAllDoorsSelected(false)
      }
      formTrigger('door_ids')
    }
  }, [selectedCount, companyTotalDoors, ds2026])

  if (ds2026) {
    // The "Entire company / Select locations" radio now lives in step 1 (below
    // the company field, as `ApplyToRadioField`); this component is just the
    // fb-ui location-picker panel (chips + table) shown on step 2 once "Select
    // locations" is chosen. Selection count drives `is_all_doors_selected` +
    // validation directly in the change handler (no count-tracking effect).
    const handleSelectedIdsChange = (ids: number[]) => {
      setHasTouchedDoors(true)
      onChangeDoorIds(ids)
      onChangeIsAllDoorsSelected(
        companyTotalDoors > 0 && ids.length >= companyTotalDoors
      )
      formTrigger('door_ids')
    }
    return (
      <div className="flex flex-col gap-3">
        <SelectLocationsPanelFbui
          chainProxyId={chainProxyId}
          companyTotalDoors={companyTotalDoors}
          selectedIds={selectedIds}
          onSelectedIdsChange={handleSelectedIdsChange}
        />
        {/* Validation error sits below the table, not above it — and only turns
            red once the user has actually touched the selection (e.g. selected
            then deselected). On first view the empty state is guided by the
            gray header prompt, not flagged as an error. */}
        {doorIdsError?.message && hasTouchedDoors && (
          <FieldHint status="error">{doorIdsError.message}</FieldHint>
        )}
      </div>
    )
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-4 border border-gray-200 mb-3">
      <div className="flex items-center">
        <span className="text-gray-800 text-base font-medium mr-6 min-w-fit">
          {radioGroupLabel}
        </span>
        <RadioButtonSelect
          control={control}
          name="apply_to"
          options={radioGroupOptions}
          horizontal
        />
      </div>
      {isSelectLocationsSelected && (
        <>
          {doorIdsError?.message && (
            <div className="text-red-500 mt-2">{doorIdsError.message}</div>
          )}
          <div className="mt-4">
            <Tabs
              tabs={[
                {
                  key: 'selected',
                  title: (
                    <div>
                      <b>Selected ({selectedCount})</b>
                    </div>
                  ),
                },
                {
                  key: 'not_selected',
                  title: (
                    <div>
                      <b>Not Selected ({notSelectedCount})</b>
                    </div>
                  ),
                },
              ]}
              selectedTab={selectedTab}
              setSelectedTab={(value) => {
                setSelectedTab(value)
              }}
              tabStyle={{ width: '180px' }}
            />
            <div className={selectedTab !== 'selected' ? 'hidden' : ''}>
              <SelectLocationsForDealTable
                chainProxyId={chainProxyId}
                tableKey={tableKey}
                filterIdentifierModifier="-selected"
                type="selected"
                setTotalRowsCount={setSelectedCount}
                selectedIds={selectedIds}
                onSelectedIdsChange={onChangeDoorIds}
              />
            </div>
            <div className={selectedTab !== 'not_selected' ? 'hidden' : ''}>
              <SelectLocationsForDealTable
                chainProxyId={chainProxyId}
                tableKey={tableKey}
                filterIdentifierModifier="-unselected"
                type="not_selected"
                setTotalRowsCount={setNotSelectedCount}
                selectedIds={selectedIds}
                onSelectedIdsChange={onChangeDoorIds}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
